/** @jsxImportSource @emotion/react */
import { css, keyframes, useTheme } from "@emotion/react";
import React, { useEffect, useState, useRef } from "react";
import { useLanguage } from "../../../LanguageContext";
import { Button, ButtonIcon, Icon, ProgressCircle, Textarea, useToast } from "@coreai/component-library";
import { saveFeedback, saveFeedbackPropTypes, TEntityTypes, TFeedbackDetails } from "../../../services/feedbackService";
import FilterChipSelector from "../../../components/CommonUi/FilterChipSelector/FilterChipSelector";
import { useFeedback } from "context/ui/FeedbackModalContext";

interface DividerWithTextProps {
    showFeedbackForm: boolean;
    handleClose: () => void;
    reaction: "positive" | "negative" | null;
    feedbackPlace: string;
    feedbackDetail?: TFeedbackDetails | null;
    newFeedbackId?: string;
    feedbackId?: string;
    entityId?: string | null;
    entityType?: TEntityTypes;
    feedbackFormWraperRef?: React.RefObject<HTMLDivElement>;
}

const FeedbackForm: React.FC<DividerWithTextProps> = ({
  showFeedbackForm,
  handleClose,
  reaction,
  feedbackPlace,
  feedbackDetail,
  newFeedbackId,
  feedbackId,
  entityId,
  entityType,
  feedbackFormWraperRef,
}) => {
  const { t } = useLanguage();
  const { toast } = useToast();
  const theme = useTheme();
  const { setFeedbackDetail } = useFeedback();
  const [feedbackValue, setFeedbackValue] = useState(feedbackDetail?.content || '');
  const [showFeedbackSuccess, setShowFeedbackSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [reactionVal, setReactionVal] = useState<"positive" | "negative" | null>(null);
  const [tagValue, setTagValue] = useState('');
  const [_feedbackId, setFeedbackId] = useState(feedbackId || '');
  const textAreaRef = useRef<HTMLTextAreaElement>(null); // Explicitly type the ref

  useEffect(() => {
    if(feedbackDetail){
      setFeedbackValue(feedbackDetail?.content as string);
      setTagValue(feedbackDetail?.tags as string);
      setReactionVal(feedbackDetail?.reaction);
    }
  },[feedbackDetail]);

  useEffect(() => {
    if(reaction){
      setReactionVal(reaction);
    }
  },[reaction]);

    useEffect(() => {
      if(textAreaRef.current){
        textAreaRef.current.focus();
      }
  },[]);

  const handleFeedbackChange = (value: string | null) => {
    if (value === null) return;
    if (typeof value === "string") {
      if (value?.trim() === "" && value !== "") return;
    }
    if (value === "" || value?.trim() !== "") {
      setFeedbackValue(value);
    }
  }

  const submitFeedback = (val?: string) => {
    
    const updatedPayload: saveFeedbackPropTypes = {
      view: feedbackPlace,
      content: feedbackValue,
      reaction: reactionVal,
      tags: reactionVal === "negative" ? tagValue : undefined,
    };
    
    if (feedbackId) {
      updatedPayload.id = feedbackId;
    }
    
    if (entityId && entityType) {
      updatedPayload[entityType] = entityId;
    }

    setIsLoading(true);
    saveFeedback(updatedPayload)
      .then((response) => {
        setIsLoading(false);
        if (response && setFeedbackDetail) setFeedbackDetail(response);
        if (val === 'send') {
          setShowFeedbackSuccess(true);
          setTimeout(handleClose, 3000);
        } else {
          // toast({
          //   variant: 'success',
          //   title: t("feedback_generalInputConfirm"),
          // });
        }
      })
      .catch((error: any) => {
        console.log(error?.status === 404);
        if(error?.response?.status === 404 || error?.response?.status === 403){
          toast({
            variant: 'error',
            title: t("feedback_access_denied"),
          });
        }
        setIsLoading(false);
      });
  };


  useEffect(() => {
    if(tagValue){
      submitFeedback();
    }
  },[tagValue]);


  const slideIn = keyframes`
    0% { transform: translateX(100%); opacity: 0; }
    100% { transform: translateX(0); opacity: 1; }
 `;

  const slideOut = keyframes`
    0% { transform: translateX(0); opacity: 1; }
    100% { transform: translateX(100%); opacity: 0; }
 `;

  const feedbackFormStyles = css`
    background-color: #fff;
    max-width: 300px;
    display: flex;
    flex-direction: column;
    box-shadow: 0px 4px 30px 0px #00000026;
    border-radius: 8px 0 0 8px;
    z-index: 2;
    transition: transform 0.5s ease-in-out, opacity 0.5s ease-in-out;
    animation: ${showFeedbackForm ? slideIn : slideOut} 0.4s ease-in-out forwards;
  `;

  const feedbackFormHeaderStyles = css`
    border-radius: 8px 0 0 0;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.7rem 0.5rem 1.2rem;
    background-color: ${theme.colors.feedbackBackground};
    font: inherit;
    vertical-align: baseline;
  `;

  const feedbackFormBodyStyles = css`
    padding: 2rem 1.2rem;
    border-radius: 0 0 0 8px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    `;

  const headerTitle = css`
    font-family: Inter;
    font-size: 20px;
    font-weight: 600;
    line-height: 23px;
    letter-spacing: -0.01em;
    text-align: left;
    color: ${theme.colors.primary};
    `;

  const textareaStyles = css`
    width: 100%;
    margin: 6px 0;
      :focus-within {
        outline-style: none;
      }
    `;

  const titleDscOne = css`
    margin-top: 0px;
    font-family: ${theme.typography.bodyMedium.fontFamily};
    font-size: 16px;
    font-weight: 400;
    line-height: 20.8px;
    letter-spacing: -0.01em;
    text-align: left;
    margin-bottom: 10px;
    width: 100%;
    `;

  const titleDscTwo = css`
    margin-top: 0px;w
    font-size: 1rem;
    font-weight: 400;
    line-height: 20.8px;
    text-align: left;
    color: #595959;
    margin-bottom: 30px;
  `;

  const buttonStyles = css`
    width: 100%;
    margin: 6px 0;
    background-color: ${theme.colors.feedbackTitleColor};
  `;

  const feedbackSuccess = css`
    height: 418px;
    padding: 2rem 1.2rem;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
  `;

  const iconCss = css`
    color: ${theme.colors.feedbackTitleColor};
    margin-bottom: 20px;
  `;

  const titleDscOneSuccess = css`
    font-family: Inter;
    font-size: 16px;
    font-weight: 600;
    line-height: 20.8px;
    letter-spacing: -0.01em;
    text-align: center;
    margin-bottom: 20px;
    color: #000;
  `;

  const iconMainWrapper = css`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    margin-bottom: 32px;
  `;

  const iconWrapperStyle = css`
    color: ${theme.colors.primary};
    height: 72px;
    width: 72px;
    // cursor: pointer;
    padding: 16px;
    border-radius: 50%;
    border: 2px solid ${theme.colors.primary};
    transition: background-color 0.3s ease, color 0.3s ease;
    // &:hover {
    //   background-color: ${theme.colors.primary};
    //   >div {
    //    color: #ffffff;
    //    }
    // }
  `;

  const activeIconStyle = css`
    background-color: ${theme.colors.primary};
    color: #ffffff;
  `;

  const negativeTitleStyle = css`
    margin-top: 0px;
    margin-bottom: 10px;
    font-family: Volte;
    font-size: 16px;
    font-weight: 600;
    line-height: 20.8px;
    letter-spacing: -0.01em;
    text-align: center;
  `;

  const tagsWrapperStyle = css`
    margin-bottom: 10px;
  `;

  const getFeedbackPlace = (feedbackPlace: string): string => {
    switch (feedbackPlace.toLowerCase()) {
      case "description":
        return "feedback_description";
      case "analysis":
        return "feedback_analysis";
      case "summary":
        return "feedback_summary";
      default:
        return "feedback_analysis";
    }
  };

  return (
    <div css={feedbackFormStyles} ref={feedbackFormWraperRef}>
        <div css={feedbackFormHeaderStyles}>
            <div css={headerTitle}>{t("feedback_generalTrigger")}</div>
            <ButtonIcon 
                variant={"secondary"}
                size="xs"
                icon={"x-close"}
                role="button"
                tabIndex={3}
                onClick={() => handleClose()}
            />
        </div>
        {showFeedbackSuccess ? 
          <div css={feedbackSuccess}>
            <Icon css={iconCss} size="5xl" name="check-circle-filled" />
            <p css={titleDscOneSuccess}>{t("feedback_generalInputThx")}</p>
            <div css={titleDscTwo}>{t("feedback_generalInputConfirm")}</div>
          </div> 
          :
          <div css={feedbackFormBodyStyles}>
            {reactionVal === null ?
            <>
            <p css={titleDscOne}>{t("feedback_generalNewfeedback")}</p>
            <p css={titleDscTwo}>{t("feedback_generalFormLabel")}</p>
            </> :
            <>
              <div css={iconMainWrapper}>
                <div css={[iconWrapperStyle, reactionVal === 'positive' && activeIconStyle]}><Icon name="thumbs-up" size="2xl"/></div>
                <div css={[iconWrapperStyle, reactionVal === 'negative' && activeIconStyle]}><Icon name="thumbs-down" size="2xl"/></div>
              </div>
              {reactionVal === 'negative' && 
                <div css={tagsWrapperStyle}>
                  <p css={negativeTitleStyle}>{t("feedback_what_wrong")} {t(getFeedbackPlace(feedbackPlace))} ?</p>
                  <FilterChipSelector
                    items={["Vague", "Wrong", "Irrelevant"]} 
                    selectedTag={tagValue} 
                    onTagChange={(tag) => setTagValue(tag)} 
                  />
                </div>
              }
              <p css={titleDscOne} style={{fontWeight: 600}}>{t("feedback_additional_text")}</p>
            </>}


            <Textarea
                name="feedbackMsg"
                placeholder={t("share_feedback")}
                css={textareaStyles}
                value={feedbackValue}
                onChange={(e) => handleFeedbackChange(e.target.value)}
                ref={textAreaRef}
            />
            <Button
                name="feedbackSend"
                variant={"brand"}
                size='sm'
                onClick={() => submitFeedback('send')}
                icon={"send"}
                disabled={feedbackValue?.trim() === "" || feedbackValue === null || isLoading}
                css={buttonStyles}
                className="brandPrimaryBtn"
            >
              {isLoading ? (
                <ProgressCircle size="xxs" color={theme.colors.primary} />
              ) : (
                t("core_send")
              )}
            </Button>
          </div> 
        }
    </div>
  );
};

export default FeedbackForm;
